import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { trpc } from "../../lib/trpc";
import { Icon } from "../ui/Icon";
import { SearchSelect } from "../ui/SearchSelect";
import { ToggleSwitch } from "../ui/ToggleSwitch";
import "./ManualLineBuilder.css";

/**
 * Manuel sevkiyat lot tablosu — sipariş seç → o siparişin KALAN (sevk edilebilir) TÜM lotlarını tabloda göster.
 * Her lot pick list gibi "yüklenecek" toggle'lı + miktarlı gelir (varsayılan: kalanı FEFO ile dolduracak şekilde
 * AÇIK). Kullanıcı işaretleyip miktarları düzenler → "Seçili Lotları Ekle" TEK transaksiyonda (addLines) ekler.
 * Farklı siparişler peş peşe eklenebilir (çok-siparişli sevkiyat). Kalan = sipariş − sevk − açık taslak vaatleri.
 */
function fmtQty(n: number): string { return new Intl.NumberFormat("tr-TR", { maximumFractionDigits: 3 }).format(n); }
function fmtSkt(d: string | null): string { return d ? new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "2-digit" }).format(new Date(d)) : "—"; }
const r4 = (n: number) => Math.round(n * 1e4) / 1e4;

const STATUS_META: Record<string, { label: string; cls: string }> = {
  ready: { label: "Sevke hazır", cls: "b-success" },
  in_production: { label: "Üretimde", cls: "b-warn" },
  confirmed: { label: "Onaylı", cls: "b-info" },
};

type Row = {
  key: string; orderLineId: string; lotId: string; locationId: string;
  itemCode: string; itemName: string; uom: string;
  lotCode: string; locationCode: string; available: number; expiresAt: string | null;
  defOn: boolean; defQty: number;
};

/**
 * ATANAMAYAN LOT SEBEPLERİ — ekranda "neden yok" sorusunun cevabı.
 *
 * 7 Ağu 2026 canlı olayı: mal kabulle giren 5 adet mamul siparişe ATANDI, ama satırın
 * kalanı 0'a düştüğü için lot tablosu boşaldı ve ekran "atanacak lot yok" dedi. Kullanıcı
 * stoğun girmediğini sanıp aynı ürünü bir kez daha üretti — 5 adetlik sipariş için 10 adet
 * mamul ve boşuna hammadde tüketimi. Sessiz eleme yerine sebep yazılır.
 *
 * `tone` = renk SINIFI, ikon ve metin birlikte gelir (CLAUDE.md ✅#5: renk tek başına
 * anlam taşımaz). "info" düzeltilecek bir şey değil (bilgi), "warn" kullanıcının
 * müdahale edebileceği durum.
 */
const BLOCK_META: Record<string, { label: string; icon: string; tone: "info" | "warn" }> = {
  assigned_here:   { label: "Bu sevkiyata atandı",     icon: "check",         tone: "info" },
  other_draft:     { label: "Başka taslakta",          icon: "truck",         tone: "warn" },
  foreign_earmark: { label: "Başka siparişe ayrılmış", icon: "lock",          tone: "warn" },
  not_released:    { label: "Serbest değil",           icon: "alertTriangle", tone: "warn" },
  expired:         { label: "SKT geçmiş",              icon: "clock",         tone: "warn" },
};
function blockDetail(reason: string, detail: string | null, uom: string): string | null {
  if (!detail) return null;
  if (reason === "assigned_here") return `${fmtQty(Number(detail))} ${uom}`;
  if (reason === "expired") return fmtSkt(detail);
  return detail;
}

export function ManualLineBuilder({ shipmentId, onKaydedilmemis }: {
  shipmentId: string;
  /** İşaretlenip henüz EKLENMEMİŞ lot seçimi ("" = yok). Çekmecenin "Boş Planı Sil" kararı için. */
  onKaydedilmemis?: (ozet: string) => void;
}) {
  const qc = useQueryClient();
  const [orderId, setOrderId] = useState<string>("");
  const [picks, setPicks] = useState<Record<string, { on: boolean; qty: string }>>({});
  const [err, setErr] = useState<string | null>(null);

  const ordersQ = trpc.shipment.manualShippableOrders.useQuery(undefined, { staleTime: 5_000 });
  const linesQ = trpc.shipment.orderShippableLines.useQuery({ orderId, shipmentId }, { enabled: !!orderId, staleTime: 3_000 });
  const addMut = trpc.shipment.addLines.useMutation({ onSuccess: () => qc.invalidateQueries() });

  const orderOpts = useMemo(
    () => (ordersQ.data ?? []).map((o) => ({ value: o.id, label: o.code, desc: o.customerName, tail: STATUS_META[o.status]?.label })),
    [ordersQ.data],
  );
  const selectedOrder = (ordersQ.data ?? []).find((o) => o.id === orderId);
  const lines = linesQ.data?.lines ?? [];

  // Kalan lotları düz tabloya aç; her satır varsayılanı = kalanı FEFO ile doldur (dolan lot AÇIK, taşan KAPALI).
  // Aynı lot birden çok sipariş satırında görünebilir (aynı ürünün 2 satırı → AYNI earmark lot listesi; available
  // lot-bazlı, satır-bazlı DEĞİL). Varsayılan dağıtımı lot|lokasyon bazında satırlar arasında PAYLAŞTIR ki iki satır
  // aynı lotu fazla dağıtıp batch'i toptan reddettirmesin.
  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    const usedByLot = new Map<string, number>(); // lotId|locationId → önceki satırlara ayrılan varsayılan
    for (const line of lines) {
      // Uç artık TÜM satırları döndürüyor (tamamen sevk edilmiş olanlar da özet şeridinde
      // görünsün diye). Onlar için LOT SATIRI ÜRETME: kalanı 0 olan satıra eklenen her lot
      // sunucuda "fazla sevkiyat" ile reddedilir ve kullanıcı sebebini anlamazdı.
      if (line.remaining <= 0.0001) continue;
      let left = line.remaining;
      for (const lot of line.lots) {
        const lk = `${lot.lotId}|${lot.locationId}`;
        const freeAvail = Math.max(0, lot.available - (usedByLot.get(lk) ?? 0));
        const q = Math.max(0, Math.min(freeAvail, left));
        out.push({
          key: `${line.orderLineId}|${lot.lotId}|${lot.locationId}`,
          orderLineId: line.orderLineId, lotId: lot.lotId, locationId: lot.locationId,
          itemCode: line.itemCode, itemName: line.itemName, uom: line.uom,
          lotCode: lot.lotCode, locationCode: lot.locationCode, available: lot.available, expiresAt: lot.expiresAt,
          defOn: q > 0.0001, defQty: r4(q),
        });
        usedByLot.set(lk, (usedByLot.get(lk) ?? 0) + q);
        left = Math.max(0, left - q);
      }
    }
    return out;
  }, [lines]);

  /**
   * ATANAMAYAN LOTLAR — satırlar arasında TEKİLLEŞTİRİLİR.
   *
   * Aynı ürünün iki sipariş satırı varsa aynı lot iki kez engellenmiş görünürdü; kullanıcı
   * "iki lot mu var" diye okur. Anahtar lot|lokasyon|sebep: aynı lot iki FARKLI sebeple
   * (biri bu planda, biri SKT) engelliyse ikisi de gösterilir — sebepler farklı bilgidir.
   */
  const blocked = useMemo(() => {
    const map = new Map<string, { lotCode: string; locationCode: string; qty: number; reason: string; detail: string | null; itemName: string; itemCode: string; uom: string }>();
    for (const line of lines) {
      for (const b of line.blockedLots ?? []) {
        const k = `${b.lotId}|${b.locationId}|${b.reason}`;
        if (!map.has(k)) {
          map.set(k, {
            lotCode: b.lotCode, locationCode: b.locationCode, qty: b.qty,
            reason: b.reason, detail: b.detail,
            itemName: line.itemName, itemCode: line.itemCode, uom: line.uom,
          });
        }
      }
    }
    return [...map.values()];
  }, [lines]);

  // Sipariş değişince kullanıcı override'larını sıfırla (taze varsayılanlar). Ekleme sonrası da (refetch) sıfırlanır.
  useEffect(() => { setPicks({}); setErr(null); }, [orderId, linesQ.dataUpdatedAt]);

  const effOn = (r: Row) => picks[r.key]?.on ?? r.defOn;
  const effQty = (r: Row) => picks[r.key]?.qty ?? (r.defQty > 0 ? String(r.defQty) : "");
  const setRow = (r: Row, patch: Partial<{ on: boolean; qty: string }>) =>
    setPicks((s) => ({ ...s, [r.key]: { on: patch.on ?? effOn(r), qty: patch.qty ?? effQty(r) } }));

  function toggleRow(r: Row, on: boolean) {
    // Kapalı→açık ve miktar boşsa müsaite doldur (kullanıcı azaltabilir; backend kalan tavanını zorlar).
    const cur = effQty(r);
    const qty = on && (!cur || Number(cur.replace(",", ".")) <= 0) ? String(r4(r.available)) : cur;
    setRow(r, { on, qty });
  }

  const selectedRows = rows.filter((r) => effOn(r));
  const totalSel = selectedRows.reduce((s, r) => s + (Number((effQty(r) || "0").replace(",", ".")) || 0), 0);

  /**
   * Sunucunun GÖRMEDİĞİ seçim: sipariş seçilip lotlar işaretlendi ama "Ekle"ye basılmadı (ya da ekleme
   * sürüyor). Plan kayıtta boş olsa da kullanıcı lot atamak üzere — çekmece silmeyi ONAYA düşürür.
   * Yalnız temkinli yön: onaysız silmeyi açmaz, sunucudaki tek tanım (BOS_PLAN_KOSULU) değişmez.
   */
  const kaydedilmemisOzet = addMut.isPending
    ? "lot ekleniyor"
    : orderId && selectedRows.length > 0 ? `${selectedRows.length} lot işaretli, eklenmedi` : "";
  useEffect(() => {
    onKaydedilmemis?.(kaydedilmemisOzet);
    return () => onKaydedilmemis?.(""); // kart kalkınca üstte bayat "kirli" kalmasın
  }, [kaydedilmemisOzet, onKaydedilmemis]);

  function setAll(on: boolean) {
    setPicks(() => {
      const n: Record<string, { on: boolean; qty: string }> = {};
      for (const r of rows) n[r.key] = { on, qty: on ? (r.defQty > 0 ? String(r.defQty) : String(r4(r.available))) : effQty(r) };
      return n;
    });
  }

  async function submit() {
    setErr(null);
    const toAdd: Array<{ orderLineId: string; lotId: string; locationId: string; qty: number }> = [];
    for (const r of selectedRows) {
      const qty = Number((effQty(r) || "").replace(",", "."));
      if (!Number.isFinite(qty) || qty <= 0) { setErr(`${r.itemName} · ${r.lotCode}: geçerli miktar girin.`); return; }
      if (qty > r.available + 0.0001) { setErr(`${r.lotCode}: en fazla ${fmtQty(r.available)} ${r.uom} atanabilir.`); return; }
      toAdd.push({ orderLineId: r.orderLineId, lotId: r.lotId, locationId: r.locationId, qty });
    }
    if (toAdd.length === 0) { setErr("En az bir lot işaretleyin."); return; }
    // Satır (ürün) bazında kalan aşımını erken yakala.
    for (const line of lines) {
      const sum = toAdd.filter((x) => x.orderLineId === line.orderLineId).reduce((s, x) => s + x.qty, 0);
      if (sum > line.remaining + 0.0001) { setErr(`${line.itemName}: kalan ${fmtQty(line.remaining)} ${line.uom} aşıldı (${fmtQty(sum)} işaretlendi).`); return; }
    }
    // Lot|lokasyon bazında: aynı lotu birden çok satır (aynı ürünün iki sipariş satırı) işaretlediyse toplam ≤
    // lotun müsaiti — paylaşılan-lot fazla-vaadini backend all-or-nothing reddine düşmeden net mesajla yakala.
    const byLot = new Map<string, { sum: number; avail: number; code: string; uom: string }>();
    for (const x of toAdd) {
      const r = selectedRows.find((s) => s.orderLineId === x.orderLineId && s.lotId === x.lotId && s.locationId === x.locationId)!;
      const lk = `${x.lotId}|${x.locationId}`;
      const cur = byLot.get(lk) ?? { sum: 0, avail: r.available, code: r.lotCode, uom: r.uom };
      cur.sum += x.qty; byLot.set(lk, cur);
    }
    for (const v of byLot.values()) {
      if (v.sum > v.avail + 0.0001) { setErr(`${v.code}: bu lottan en fazla ${fmtQty(v.avail)} ${v.uom} işaretlenebilir (${fmtQty(r4(v.sum))} işaretli).`); return; }
    }
    try {
      await addMut.mutateAsync({ shipmentId, lines: toAdd });
      setPicks({}); // refetch varsayılanları tazeler
    } catch (e) { setErr((e as Error).message); }
  }

  return (
    <div className="d-card msl-card">
      <div className="d-card-head">
        <div className="left">
          <span className="t">Sevkiyata Lot Ekle</span>
          <span className="s">sipariş seç → kalan lotları işaretle → ekle</span>
        </div>
      </div>

      <div className="d-card-body msl-pick">
        {err ? (
          <div className="form-banner-err" role="alert">
            <Icon name="alertTriangle" size={14} />
            <span>{err}</span>
          </div>
        ) : null}

        <div className="form-field msl-order-field">
          <label className="lbl">Sipariş</label>
          <SearchSelect
            value={orderId || null}
            options={orderOpts}
            onChange={(v) => { setOrderId(v ?? ""); setErr(null); }}
            icon="list"
            placeholder="Sevk edilecek sipariş seç…"
            searchPlaceholder="Sipariş / müşteri ara…"
            emptyText={ordersQ.isLoading ? "Yükleniyor…" : "Sevkiyata hazır sipariş yok"}
          />
          {selectedOrder ? (
            <div className="msl-order-meta">
              <Icon name="truck" size={13} />
              <span className="msl-cust">{selectedOrder.customerName}</span>
              {(() => {
                const m = STATUS_META[selectedOrder.status];
                if (!m) return null;
                const ic = selectedOrder.status === "ready" ? "check" : selectedOrder.status === "in_production" ? "clock" : "checkCircle";
                return (
                  <span className={`badge-pill ${m.cls}`}>
                    <Icon name={ic} size={11} />
                    {m.label}
                  </span>
                );
              })()}
            </div>
          ) : (
            <span className="help">Farklı siparişlerden lot eklenebilir; üretimi süren siparişten kısmi sevkiyat yapılabilir. Kalanlar bir sonraki sevkiyata açık kalır.</span>
          )}
        </div>
      </div>

      {orderId ? (
        <>
          {/* ═══ SİPARİŞ MİKTARLARI — lot listesinin ÜSTÜNDE ═══
              Siparişe konu TÜM kalemler burada (sevk edilmiş olanlar dahil). Aşağıdaki lot
              tablosu yalnız FİİLEN ÜRETİLMİŞ/ELDEKİ partileri gösterdiği için, elde OLMAYAN
              miktarın (eksik / üretimde) ayrıca yazılması şart: aksi halde kullanıcı kısa
              lot listesine bakıp "sipariş eksik girilmiş" sanıyordu. */}
          {!linesQ.isLoading && lines.length > 0 ? (
            <>
              <div className="fgroup-head msl-band">
                <span className="ic"><Icon name="list" size={14} /></span>
                <span className="t">Sipariş Miktarları</span>
                <span className="s">{lines.length} kalem</span>
              </div>
              <div className="d-card-body msl-sum-body">
                <table className="msl-table msl-sum">
                  <thead>
                    <tr>
                      <th>Ürün</th>
                      {/* Genişlikler İPUCU: dar çekmecede (≈626px) toplamları kabı aşarsa
                          tarayıcı ürün sütununu ezip adı 3 satıra sarıyordu (ölçüldü).
                          Sayısal içerik ~70px'e sığıyor; artan yer ürüne ve eksiğe gidiyor. */}
                      <th className="num" style={{ width: 74 }}>Sipariş</th>
                      <th className="num" style={{ width: 74 }}>Sevk</th>
                      <th className="num" style={{ width: 74 }}>Bu planda</th>
                      <th className="num" style={{ width: 74 }}>Kalan</th>
                      <th className="num" style={{ width: 78 }}>Elde (lot)</th>
                      <th className="num" style={{ width: 140 }}>Eksik</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lines.map((l) => (
                      <tr key={l.orderLineId} className={l.fullyShipped ? "msl-row-done" : l.missing > 0.0001 ? "msl-row-short" : ""}>
                        <td>
                          <span className="msl-t-name">{l.itemName}</span>
                          <span className="msl-t-code mono">{l.itemCode}</span>
                        </td>
                        <td className="mono num">{fmtQty(l.ordered)} <span className="msl-t-uom">{l.uom}</span></td>
                        <td className="mono num">{l.shipped > 0.0001 ? fmtQty(l.shipped) : <span className="msl-dim">—</span>}</td>
                        {/* "Bu planda" GERÇEKTEN bu plan: eskiden tüm açık taslakların toplamı
                            yazıyordu ve mal başka taslaktayken kullanıcı boş bir sevkiyata
                            bakıp "bu planda 5" görüyordu. Başkasının tuttuğu miktar ayrı satırda. */}
                        <td className="mono num">
                          {l.committedHere > 0.0001 ? fmtQty(l.committedHere) : <span className="msl-dim">—</span>}
                          {l.committedOther > 0.0001 ? (
                            <span className="msl-other-draft" title="Başka açık taslak sevkiyatlarda ayrılmış">
                              +{fmtQty(l.committedOther)} başka taslak
                            </span>
                          ) : null}
                        </td>
                        <td className="mono num"><b>{fmtQty(l.remaining)}</b></td>
                        <td className="mono num">{l.availableQty > 0.0001 ? fmtQty(l.availableQty) : <span className="msl-dim">0</span>}</td>
                        <td className="num">
                          {l.fullyShipped ? (
                            <span className="msl-done"><Icon name="check" size={13} />Tamamlandı</span>
                          ) : l.missing > 0.0001 ? (
                            <span className="msl-short">
                              <Icon name="alertTriangle" size={13} />
                              <b className="mono">{fmtQty(l.missing)}</b> {l.uom}
                              {/* Eksiğin üretimdeki kısmı: "yok" ile "geliyor" AYNI ŞEY DEĞİL. */}
                              {l.inProduction > 0.0001
                                ? <span className="msl-inprod">· <b className="mono">{fmtQty(Math.min(l.missing, l.inProduction))}</b> üretimde</span>
                                : <span className="msl-noprod">· üretim yok</span>}
                            </span>
                          ) : (
                            <span className="msl-ok"><Icon name="check" size={13} />Elde yeterli</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}

          <div className="fgroup-head msl-band">
            <span className="ic"><Icon name="boxes" size={14} /></span>
            <span className="t">Yüklenecek Lotlar</span>
            {!linesQ.isLoading && rows.length > 0 ? <span className="s">{fmtQty(rows.length)} lot · {fmtQty(selectedRows.length)} seçili</span> : null}
            {rows.length > 0 ? (
              <div className="msl-band-actions">
                <button type="button" className="btn" onClick={() => setAll(true)}>Tümünü seç</button>
                <button type="button" className="btn" onClick={() => setAll(false)}>Temizle</button>
              </div>
            ) : null}
          </div>

          <div className="d-card-body msl-lines-body">
            {linesQ.isLoading ? (
              <div className="msl-loading" aria-busy="true" aria-label="Lotlar yükleniyor">
                <div className="msl-skel" />
                <div className="msl-skel" />
              </div>
            ) : rows.length === 0 ? (
              /* BOŞ DURUM ARTIK "STOK YOK" DEMİYOR.
                 Engellenen lot varsa mal FİİLEN DEPODADIR; eski metin ("sevk edilebilir lot
                 yok") kullanıcıyı yeniden üretime gönderdi. Sebep listesi aşağıda. */
              <div className="empty-state msl-empty">
                <span className="ic"><Icon name={blocked.length > 0 ? "lock" : "package"} size={26} /></span>
                <p className="ttl">{blocked.length > 0 ? "Atanabilir lot yok — stok başka yerde tutuluyor" : "Atanacak lot yok"}</p>
                <p className="sub">
                  {blocked.length > 0
                    ? "Bu ürünün stoğu depoda var ama aşağıdaki sebeplerle bu satıra atanamıyor. Yeniden üretmeden önce sebebi giderin."
                    : "Bu siparişin tüm satırları sevk edilmiş/vaat edilmiş ya da henüz sevk edilebilir (siparişe üretilen, QC onaylı, SKT-uygun) lot yok."}
                </p>
              </div>
            ) : (
              <table className="msl-table">
                <thead>
                  <tr>
                    <th style={{ width: 54 }}>Yükle</th>
                    <th>Ürün</th>
                    <th style={{ width: 130 }}>Lot</th>
                    <th style={{ width: 92 }}>Lokasyon</th>
                    <th style={{ width: 96 }}>Müsait</th>
                    <th style={{ width: 132 }}>Yüklenecek</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const on = effOn(r);
                    return (
                      <tr key={r.key} className={on ? "" : "msl-row-off"}>
                        <td className="c"><ToggleSwitch checked={on} onChange={(v) => toggleRow(r, v)} ariaLabel={`${r.itemName} ${r.lotCode} yükle`} /></td>
                        <td>
                          <span className="msl-t-name">{r.itemName}</span>
                          <span className="msl-t-code mono">{r.itemCode}</span>
                        </td>
                        <td>
                          <span className="mono msl-t-lot">{r.lotCode}</span>
                          {r.expiresAt ? <span className="msl-t-skt">SKT {fmtSkt(r.expiresAt)}</span> : null}
                        </td>
                        <td className="mono">{r.locationCode}</td>
                        <td className="mono num">{fmtQty(r.available)} <span className="msl-t-uom">{r.uom}</span></td>
                        <td>
                          <div className="msl-t-qty">
                            <input
                              className="form-input"
                              type="number"
                              step="any"
                              inputMode="decimal"
                              disabled={!on}
                              aria-label={`${r.itemName} ${r.lotCode} yüklenecek miktar`}
                              value={effQty(r)}
                              max={r.available}
                              placeholder={fmtQty(r.available)}
                              onChange={(e) => setRow(r, { qty: e.target.value })}
                            />
                            <span className="msl-uom">{r.uom}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {/* ═══ ATANAMAYAN LOTLAR ═══
              Lot tablosu dolu olsa da gösterilir: "5 tane var ama 3'ü şu sebeple kilitli"
              bilgisi, ancak liste boşken gösterilirse yarım kalır. */}
          {!linesQ.isLoading && blocked.length > 0 ? (
            <>
              <div className="fgroup-head msl-band msl-band-blocked">
                <span className="ic"><Icon name="lock" size={14} /></span>
                <span className="t">Atanamayan Lotlar</span>
                <span className="s">{fmtQty(blocked.length)} lot · depoda duruyor</span>
              </div>
              <div className="d-card-body msl-blocked-body">
                <ul className="msl-blocked">
                  {blocked.map((b, i) => {
                    const m = BLOCK_META[b.reason] ?? { label: b.reason, icon: "alertTriangle", tone: "warn" as const };
                    const d = blockDetail(b.reason, b.detail, b.uom);
                    return (
                      <li key={`${b.lotCode}|${b.locationCode}|${b.reason}|${i}`} className={`msl-blocked-row t-${m.tone}`}>
                        <span className="msl-b-lot">
                          <span className="mono">{b.lotCode}</span>
                          <span className="msl-b-item">{b.itemName}</span>
                        </span>
                        <span className="mono msl-b-loc">{b.locationCode}</span>
                        <span className="mono msl-b-qty">{fmtQty(b.qty)} <span className="msl-t-uom">{b.uom}</span></span>
                        {/* Durum = ikon + METİN + renk (renk tek başına anlam taşımaz). */}
                        <span className="msl-b-reason">
                          <Icon name={m.icon} size={12} />
                          {m.label}
                          {d ? <b className="msl-b-detail">{d}</b> : null}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          ) : null}

          {rows.length > 0 ? (
            <div className="msl-foot">
              <span className="msl-foot-sum">{fmtQty(selectedRows.length)} lot · toplam {fmtQty(r4(totalSel))}</span>
              <button type="button" className="btn-md primary msl-submit" disabled={addMut.isPending || selectedRows.length === 0} onClick={submit}>
                <Icon name="plus" size={14} />
                {addMut.isPending ? "Ekleniyor…" : `Seçili ${fmtQty(selectedRows.length)} Lotu Ekle`}
              </button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
